import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { ActivityRegisterServicesRequestData } from '@core/models/swagger/activityRegistry';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { HeadersEnum } from '@core/services/api/api.service';
import { ActivityRegistryService } from '@core/services/api/http/activityRegistry.service';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import {
  getProjectId,
  getSessionId,
  UpdateServiceInformation,
  UpdateSessionId,
} from '@store/activity-registry/activity-registry';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class JwtInterceptorService implements HttpInterceptor {
  projectId: string;
  sessionId: string;

  constructor(
    private readonly store: Store,
    private readonly analyticsService: AnalyticsService,
    private readonly activityRegistryService: ActivityRegistryService
  ) {
    this.store.select(getProjectId).subscribe(projectId => (this.projectId = projectId));
    this.store.select(getSessionId).subscribe(sessionId => (this.sessionId = sessionId));
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isActivityRegistryRequest = request.url.includes(ActivityRegistryService.BASE_PATH);
    const isBusinessRequest = request.url.includes(environment.apiUrl);
    const serviceUrl = request.url.split(
      isActivityRegistryRequest ? environment.activityRegistryApiUrl : environment.apiUrl
    )[1];
    const startingTime = new Date().getTime();
    let activityRegisterServicePayload: ActivityRegisterServicesRequestData = {
      serviceInfo: {
        path: serviceUrl,
        method: request.method as any,
        isServiceResponseOK: false,
        statusCode: 0,
        requestTime: 0,
      },
    };
    return next.handle(request).pipe(
      tap(response => {
        if (response instanceof HttpResponse) {
          // Update analytic (OK)
          this.analyticsService.setErrorType('');
          this.analyticsService.setErrorMessage('');

          // Update activity registry (OK)
          activityRegisterServicePayload = {
            ...activityRegisterServicePayload,
            serviceInfo: {
              ...activityRegisterServicePayload.serviceInfo,
              statusCode: response.status,
              isServiceResponseOK: response.ok,
              requestTime: new Date().getTime() - startingTime,
            },
          };
        }
      }),
      catchError(err => {
        if (err.status) {
          // Update analytic (KO)
          this.analyticsService.setErrorType(err?.status.toString());
          this.analyticsService.setErrorMessage(err?.message);

          // Update activity registry (KO)
          if (!isActivityRegistryRequest) {
            activityRegisterServicePayload = {
              ...activityRegisterServicePayload,
              serviceInfo: {
                ...activityRegisterServicePayload.serviceInfo,
                statusCode: err.status,
                isServiceResponseOK: err.ok,
                requestTime: new Date().getTime() - startingTime,
              },
            };
            return throwError(err);
          }

          // For errors that are not Unauthorized (401)
          if (err.status !== HttpStatusCode.Unauthorized) {
            return throwError(err);
          }

          // In case update session fails, init activity registry again
          if (request.url.includes(ActivityRegistryService.UPDATE_SESSION_PATH)) {
            return this.activityRegistryService.getInit(this.projectId).pipe(
              switchMap(response => {
                this.store.dispatch(UpdateSessionId({ data: response.sessionId }));

                const that = this;
                const body = {
                  ...request.body,
                  sessionId: response.sessionId,
                } as Parameters<typeof that.activityRegistryService.postInsert>[number];

                return next.handle(
                  request.clone({
                    body,
                    setHeaders: {
                      ...request.headers,
                      [HeadersEnum.X_SESSION_ID]: response.sessionId,
                    },
                  })
                );
              })
            );
          } else {
            //  Inserting service returns 401 -> Update session
            return this.activityRegistryService.getUpdateSession(this.projectId, this.sessionId).pipe(
              switchMap(response => {
                this.store.dispatch(UpdateSessionId({ data: response.sessionId }));
                const that = this;
                const body = {
                  ...request.body,
                  sessionId: response.sessionId,
                } as Parameters<typeof that.activityRegistryService.postInsert>[number];
                return next.handle(
                  request.clone({
                    body,
                    setHeaders: {
                      ...request.headers,
                      [HeadersEnum.X_SESSION_ID]: response.sessionId,
                    },
                  })
                );
              })
            );
          }
        }
      }),
      finalize(() => {
        // For business requests only
        if (isBusinessRequest && !request.url.includes('initflow')) {
          this.store.dispatch(UpdateServiceInformation({ data: activityRegisterServicePayload }));
        }
      })
    );
  }
}
